import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Save, Upload } from "lucide-react";
import type { CmsField } from "./cmsFieldsConfig";

interface Props {
  field: CmsField;
  value: string;
  saving: boolean;
  uploading: boolean;
  onChange: (key: string, value: string) => void;
  onSave: (key: string) => void;
  onImageUpload: (key: string, file: File) => void;
  onRemoveImage?: (key: string) => void;
}

const CmsFieldRenderer = ({ field, value, saving, uploading, onChange, onSave, onImageUpload, onRemoveImage }: Props) => {
  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2 text-sm">
        <field.icon size={14} className="text-muted-foreground" /> {field.label}
      </Label>
      {field.hint && (
        <p className="text-xs text-muted-foreground/70">{field.hint}</p>
      )}

      {field.type === "text" && (
        <div className="flex gap-2">
          <Input
            value={value}
            onChange={(e) => onChange(field.key, e.target.value)}
            placeholder={field.hint || ""}
          />
          <Button onClick={() => onSave(field.key)} disabled={saving} className="gap-1 shrink-0" size="sm">
            <Save size={14} /> {saving ? "..." : "Guardar"}
          </Button>
        </div>
      )}

      {field.type === "textarea" && (
        <div className="space-y-2">
          <Textarea
            value={value}
            onChange={(e) => onChange(field.key, e.target.value)}
            rows={4}
            placeholder={field.hint || ""}
          />
          <Button onClick={() => onSave(field.key)} disabled={saving} className="gap-1" size="sm">
            <Save size={14} /> {saving ? "..." : "Guardar"}
          </Button>
        </div>
      )}

      {field.type === "image" && (
        <div className="space-y-3">
          {value && (
            <div className="relative inline-block">
              <img src={value} alt={field.label} className="h-32 w-auto rounded-xl border border-border object-cover" />
              <button
                type="button"
                onClick={() => onRemoveImage?.(field.key)}
                className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center text-xs font-bold hover:opacity-80 transition-opacity"
                title="Remover imagem"
              >
                ✕
              </button>
            </div>
          )}
          <div className="flex items-center gap-2">
            <label className="cursor-pointer inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm hover:bg-muted transition-colors">
              <Upload size={14} />
              {uploading ? "A carregar..." : "Escolher imagem"}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) onImageUpload(field.key, file);
                }}
              />
            </label>
          </div>
        </div>
      )}

      {field.type === "color" && (
        <div className="flex gap-2 items-center">
          <input
            type="color"
            value={value || "#8B2252"}
            onChange={(e) => onChange(field.key, e.target.value)}
            className="w-12 h-10 rounded-lg border border-border cursor-pointer"
          />
          <Input
            value={value}
            onChange={(e) => onChange(field.key, e.target.value)}
            placeholder="#8B2252"
            className="max-w-[140px]"
          />
          <Button onClick={() => onSave(field.key)} disabled={saving} className="gap-1 shrink-0" size="sm">
            <Save size={14} /> {saving ? "..." : "Guardar"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default CmsFieldRenderer;
